import React, { useMemo, useRef } from "react"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined"
import SortIcon from "@mui/icons-material/Sort"
import SortableRecipeCard from "./SortableRecipeCard"
import { COLORS } from "../../../../utils"

const findInsertBeforeRecipeId = (bodyEl, clientY, excludeRecipeId) => {
  const cards = Array.from(bodyEl.querySelectorAll("[data-recipe-id]"))
    .filter((el) => el.dataset.recipeId !== excludeRecipeId)
  for (const card of cards) {
    const rect = card.getBoundingClientRect()
    if (clientY < rect.top + rect.height / 2) return card.dataset.recipeId
  }
  return null
}

const RecipeDayColumn = ({
  slot, placements, onRemoveRecipe, onChooseProdDay,
  draggedRecipeId, dropTarget, onCardDragStart, onCardDragEnd, onZoneDragOver, onZoneDragLeave, onZoneDrop,
  catRank, onSortSlot,
}) => {
  const bodyRef = useRef(null)
  const isOver = dropTarget && dropTarget.slotId === slot.id
  const columnSx = { ...sx.column, ...(isOver ? sx.columnDragOver : {}) }
  const countLabel = `${placements.length} plat${placements.length > 1 ? "s" : ""}`

  const isSorted = useMemo(() => {
    for (let i = 1; i < placements.length; i++) {
      const prevRank = catRank.get(placements[i - 1].recipe.cat) ?? Number.MAX_SAFE_INTEGER
      const curRank = catRank.get(placements[i].recipe.cat) ?? Number.MAX_SAFE_INTEGER
      if (curRank < prevRank) return false
    }
    return true
  }, [placements, catRank])

  const canSort = placements.length > 1 && !isSorted

  const handleDragOver = (e) => {
    e.preventDefault()
    if (!bodyRef.current) return
    const insertBeforeRecipeId = findInsertBeforeRecipeId(bodyRef.current, e.clientY, draggedRecipeId)
    onZoneDragOver(slot.id, insertBeforeRecipeId)
  }

  const handleDragLeave = (e) => {
    if (bodyRef.current && bodyRef.current.contains(e.relatedTarget)) return
    onZoneDragLeave(slot.id)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    onZoneDrop(slot.id)
  }

  return (
    <Box className="flexColumn stretch flex1" sx={columnSx}>
      <Box className="flexRow center" sx={sx.header}>
        <LocalShippingOutlinedIcon sx={sx.headerIcon} />
        <Box className="flex1" sx={{ minWidth: 0 }}>
          <Typography className="ellipsis" sx={sx.headerLabel}>{slot.dcName}</Typography>
        </Box>
        {canSort && (
          <Tooltip title="Trier par catégorie">
            <IconButton size="small" onClick={() => onSortSlot(slot.id)} aria-label="Trier par catégorie">
              <SortIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Typography sx={sx.count}>{countLabel}</Typography>
      </Box>
      <Box
        ref={bodyRef}
        className="flexColumn stretch flex1"
        sx={sx.body}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {placements.map((placement, index) => {
          const recipeId = placement.recipe.recipeId
          const showGroupHeader = isSorted && (index === 0 || placements[index - 1].recipe.cat !== placement.recipe.cat)
          return (
            <React.Fragment key={recipeId}>
              {showGroupHeader && <Typography sx={sx.groupHeader}>{placement.recipe.catLabel}</Typography>}
              <Box sx={sx.cardSlot}>
                {isOver && dropTarget.insertBeforeRecipeId === recipeId && <Box sx={sx.dropIndicator} />}
                <SortableRecipeCard
                  recipeId={recipeId}
                  placement={placement}
                  onDragStart={() => onCardDragStart({ recipe: placement.recipe, sourceSlotId: slot.id })}
                  onDragEnd={onCardDragEnd}
                  onRemove={() => onRemoveRecipe(slot.id, recipeId)}
                  onChooseProdDay={(date) => onChooseProdDay(slot.id, recipeId, date)}
                />
              </Box>
            </React.Fragment>
          )
        })}
        {isOver && dropTarget.insertBeforeRecipeId === null && <Box sx={sx.endDropIndicator} />}
      </Box>
    </Box>
  )
}

export default RecipeDayColumn

const sx = {
  column: {
    minWidth: 0, border: "1px solid", borderColor: COLORS.DEFAULT_GREY, bgcolor: COLORS.WHITE,
    transition: "border-color .15s, background-color .15s",
  },
  columnDragOver: { borderColor: COLORS.PRIMARY_COLOR, bgcolor: COLORS.PRIMARY_BLUE_20 },
  header: { gap: "6px", p: "10px 12px", bgcolor: COLORS.PRIMARY_BLUE_50, borderBottom: "1px solid", borderColor: COLORS.DEFAULT_GREY },
  headerIcon: { fontSize: 18, color: COLORS.GREY_900, flexShrink: 0 },
  headerLabel: { fontSize: 14, fontWeight: 400, color: COLORS.GREY_900 },
  count: {
    fontSize: 14, color: COLORS.GREY_900, bgcolor: COLORS.WHITE, borderRadius: "999px",
    px: "8px", py: "2px", flexShrink: 0, whiteSpace: "nowrap",
  },
  body: { p: 1, gap: "8px" },
  groupHeader: {
    fontSize: 12, fontWeight: 600, color: COLORS.DARK_GREY, textTransform: "uppercase",
    letterSpacing: ".04em", mt: "4px",
  },
  cardSlot: { position: "relative" },
  dropIndicator: {
    position: "absolute", top: "-5px", left: 0, right: 0, height: "3px",
    borderRadius: "999px", bgcolor: COLORS.PRIMARY_COLOR, pointerEvents: "none", zIndex: 2,
  },
  endDropIndicator: { height: "3px", borderRadius: "999px", bgcolor: COLORS.PRIMARY_COLOR, flexShrink: 0 },
}
